import { createSelector } from 'reselect';
import curryRight from 'lodash/curryRight';
import flow from 'lodash/flow';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import { NO_PRACTICE } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import type { WorkCategoryAllSupported } from '@atlassian/jira-servicedesk-work-category/src/controllers/use-work-category-all-supported/types.tsx';
import type {
	TicketTypeData,
	TicketType,
	AppProps,
	NewTicketType,
	PendingDataState,
	FilterOptions,
	CollateOptions,
	RequestTypeFormData,
	ProjectWorkflowScheme,
} from '../../common/types.tsx';
import {
	filterTicketTypesByIssueTypes,
	filterTicketTypesByGroups,
	filterTicketTypesByText,
	filterUnmappedRequestType,
	filterTicketTypesByCategory,
} from './utils.tsx';

export const pendingTicketTypeCreationSelector = (
	state: TicketTypeData,
): PendingDataState<NewTicketType> => state.pendingTicketTypeCreation;

export const pendingTicketTypeDeletionSelector = (
	state: TicketTypeData,
): PendingDataState<string> => state.pendingTicketTypeDeletion;

const ticketTypesSelector = createSelector(
	(state: TicketTypeData) => state.ticketTypes,
	(ticketTypesMap) => {
		const keys = Object.keys(ticketTypesMap);
		if (keys.length === 0) {
			return null;
		}
		return keys.map((key) => ticketTypesMap[key] && ticketTypesMap[key].currentState);
	},
);

export const unmappedRequestTypesSelector = createSelector(
	ticketTypesSelector,
	filterUnmappedRequestType,
);

const ticketTypeUpdateSelector = createSelector(
	(state: TicketTypeData) => state.ticketTypes,
	(state: TicketTypeData, ticketTypeId: number) => ticketTypeId,
	(ticketTypesMap, ticketTypeId) => {
		if (!ticketTypesMap || Object.keys(ticketTypesMap).length < 0) {
			return null;
		}
		const ticketType = ticketTypesMap[String(ticketTypeId)];

		return ticketType && ticketType.updateState;
	},
);

export const ticketTypesByPracticeSelector = createSelector(
	(state: TicketTypeData) => ticketTypesSelector(state),
	(state) => unmappedRequestTypesSelector(state),
	(state: TicketTypeData, practice: WorkCategoryAllSupported | undefined | null) => practice,
	(allTicketTypes, unmappedRequestTypes, practice) => {
		if (practice == null || practice === NO_PRACTICE) {
			return unmappedRequestTypes;
		}

		const filteredTicketTypes =
			allTicketTypes && allTicketTypes.filter(({ practices }) => practices.includes(practice));

		return !filteredTicketTypes || filteredTicketTypes.length === 0 ? null : filteredTicketTypes;
	},
);

export const ticketTypeSelector = createSelector<
	TicketTypeData,
	number,
	TicketType[] | null,
	number,
	TicketType | null
>(
	(state) => ticketTypesSelector(state),
	(state, ticketTypeId) => ticketTypeId,
	(ticketTypes, ticketTypeId) => {
		if (!ticketTypes) {
			return null;
		}
		return ticketTypes.find(({ id }) => id === ticketTypeId) || null;
	},
);

export const changeWorkflowSelector = createSelector(
	(state: TicketTypeData) => state,
	(state: TicketTypeData) => {
		if (state.changeWorkflow.requestTypeId !== null) {
			return {
				isShowing: state.changeWorkflow.isShowing,
				requestType: ticketTypeSelector(state, state.changeWorkflow.requestTypeId),
			};
		}
		return { isShowing: state.changeWorkflow.isShowing, requestType: null };
	},
);

export const ticketTypeSetNameErrorSelector = createSelector(
	(state: TicketTypeData, ticketTypeId: number) => ticketTypeUpdateSelector(state, ticketTypeId),
	(updateState) => {
		const namePendingState = updateState && updateState.name;
		if (namePendingState) {
			return namePendingState.error;
		}
		return null;
	},
);

// ARMOR-1126 - use this
export const ticketTypeSetIconErrorSelector = createSelector(
	(state: TicketTypeData, ticketTypeId: number) => ticketTypeUpdateSelector(state, ticketTypeId),
	(updateState) => {
		const iconPendingState = updateState && updateState.icon;
		if (iconPendingState) {
			return iconPendingState.error;
		}
		return null;
	},
);

export const filteredTicketTypesSelector = createSelector(
	(state: TicketTypeData, { practice }: FilterOptions) =>
		ticketTypesByPracticeSelector(state, practice),
	(state, filterOptions) => filterOptions,
	(ticketTypesByPractice, filterOptions) => {
		if (isEmpty(ticketTypesByPractice)) {
			return null;
		}

		const { issueTypes, groups, text } = filterOptions;

		const { requestTypeCategories } = filterOptions;

		return flow([
			curryRight(filterTicketTypesByIssueTypes)(issueTypes),
			curryRight(filterTicketTypesByGroups)(groups),
			curryRight(filterTicketTypesByText)(text),
			curryRight(filterTicketTypesByCategory)(requestTypeCategories),
		])(ticketTypesByPractice);
	},
);

export const collatedTicketTypesSelector = createSelector(
	(state: TicketTypeData, { filters }: CollateOptions) =>
		filters ? filteredTicketTypesSelector(state, filters) : ticketTypesSelector(state),
	(state, { sort }) => sort,
	(filteredTicketTypes, sort) => {
		if (filteredTicketTypes) {
			return sort ? orderBy(filteredTicketTypes, sort.iteratees, sort.orders) : filteredTicketTypes;
		}

		return null;
	},
);

export const collatedUnmappedRequestTypesSelector = createSelector(
	collatedTicketTypesSelector,
	filterUnmappedRequestType,
);

export const appPropsSelector = (state: TicketTypeData): AppProps => state.appProps;

export const requestTypeFormData = (state: TicketTypeData): RequestTypeFormData | null =>
	state.requestTypeFormData.data;

export const projectWorkflowSchemeSelector = (state: TicketTypeData): ProjectWorkflowScheme =>
	state.projectWorkflowScheme;
